

  
const content = `# Deploy SecureAI with Docker
  
The OnData SecureAI product is packaged into two Docker container images: 1) OnData SecureAI Application image; and 2) OnData MCP Server image. The SecureAI App image contains 
the SecureAI web application that runs business logic and interfaces with the GenAI LLM API. The OnData MCP Server image is used to connect with various data sources. You will 
need to deploy one Docker container instance for each data source. A full SecureAI deployment may consist of one SecureAI App container and one or more MCP Server containers, 
with one MCP Server container for each data source. You can use any mainstream orchestration engine, such as Docker Compose or Kubernetes, to deploy and manage the Docker 
containers for the SecureAI solution. In this guide, we will assume Docker Compose is used for container orchestration.  
  
Here are the steps to deploy the SecureAI solution using Docker container images:  
* Install prerequisites
* Download SecureAI container images from OnData  
* Register SecureAI App and data sources with OnData
* Configure and deploy SecureAI containers with Docker Compose
  
## Step 1 - Install Prerequisites
The computer or server used to host the SecureAI solution must meet the follow prerequisites. 
* Docker Engine: In order to download and deploy SecureAI container images, you will need to have a Docker engine running on your server or computer. You can follow the instructions on the 
Docker website to install the [**Docker Engine**](https://docs.docker.com/engine/install/). For production deployment, we recommend using RHEL, Ubuntu or any other Linux platform. For trial 
or pilot purposes, you may use [**Docker Desktop**](https://docs.docker.com/desktop/), which comes with the Docker engine, on a Windows Professional 10/11 computer. **Note**: For 
installation on Linux based platforms, you may need to add the current user to the \`docker\` group in order to go through the installation process  
\`\`\`bash
# 1. Add the current user to the docker group
sudo usermod -aG docker $USER

# 2. Log out and log back in, or restart the system. This step is required; otherwise, the group permissions will not take effect
\`\`\`

* PostgreSQL Database: The SecureAI App needs to connect with two PostgreSQL databases for storing user data and processing status. You may use the \`pgAdmin\` tool or the \`psql\` interactive 
shell to create two empty databases named \`db_secureai\` and \`db_secureai_hangfire\`, using default settings. After the databases are created, build database connection strings with 
appropriate credentials so that they can be used to connect to the newly created databases with full access from the hosting server
* SSL Certificate: The SecureAI App needs to have an SSL certificate installed so that all communication is handled with the HTTPS protocol. You will need an SSL certificate in 
the .pfx file format and a password for that certificate. **Note**: For trial or pilot purposes, you may use the command lines below to generate a self-signed SSL certificate. For details 
on how to use OpenSSL, please refer to the OpenSSL documentation on [Creating SSL Certificate](https://docs.openssl.org/3.0/man1/openssl-req/) and 
[Generating PFX File](https://docs.openssl.org/3.0/man1/openssl-pkcs12/)  

\`\`\`bash
# Creating SSL certificate
openssl req -x509 -newkey rsa:2048 -keyout secureai_test_key.pem -out secureai_test_cert.pem -days 365 -nodes

# Generating PFX file
openssl pkcs12 -export -out secureai_test_cert.pfx -inkey secureai_test_key.pem -in secureai_test_cert.pem -passout pass:<PFX_Password>

\`\`\`
  
## Step 2 - Download SecureAI Images
Below are the steps to download SecureAI images from OnData Portal.  
a. Login to the OnData Portal  
b. Navigate to \`Settings\` -> \`SecureAI\`  
c. Under the \`SecureAI Images\` tab, follow the instructions there to generate a new token password for accessing the OnData container registry  
d. Launch a command prompt and run the following command. Use the token name from the OnData Portal to replace the <TOKEN_NAME> in the command line, and enter the generated token 
password to login to the OnData container registry  
  
\`\`\`bash
docker login --username <TOKEN_NAME> ondataprod.azurecr.us

\`\`\`
  
e. Pull both the OnData SecureAI App image and the OnData MCP Server image from the container registry  

\`\`\`bash
docker pull ondataprod.azurecr.us/products/ondata-secureai-app:latest
docker pull ondataprod.azurecr.us/products/ondata-secureai-mcpserver:latest

\`\`\`
  
f. SecureAI uses the \`xberg\` image to perform text extraction and Optical Character Scan (OCR) on documents as needed. You may pull that image from the container registry as well  

\`\`\`bash
docker pull ondataprod.azurecr.us/products/xberg:latest

\`\`\`

## Step 3 - Register SecureAI App and Data Sources
Before deploying the SecureAI App and associated MCP Servers for all data sources, you need to register them with the OnData Portal first. Below are the registration steps:  
a. Login to the OnData Portal  
b. Navigate to \`Settings\` -> \`SecureAI\`  
c. Select the \`Registration Code\` tab, and click on the \`Add Registration Code\` button under the \`SecureAI App Registration\` section to get a registration code for the 
SecureAI App  
d. Launch a PowerShell or Bash command prompt and run the following command to register the SecureAI App. Use the registration code you have just created to replace the 
<Registration_Code> in the command line. As the registration information needs to be stored in a registration output file, please replace the <Registration_Output_File> in the 
command line with a full path to the designated registration output file, such as \`C:/SecureAI/Secrets/secureai_app_info.txt\`. Please note that the folder for the 
registration output file (\`C:/SecureAI/Secrets/\` in this example) should already exist on your file system. If not, please create that folder first  

PowerShell:
\`\`\`bash
@{ content= $(docker run --rm -e REGISTRATION_CODE=<Registration_Code> ondataprod.azurecr.us/products/ondata-secureai-app:latest); success= $? } | \`
%{ if ($_.success) { Set-Content -Path "<Registration_Output_File>" -Value $_.content; Write-Host "SecureAI App registration succeeded!" } else { Write-Error $_.content } }

\`\`\`
Bash:
\`\`\`bash
content=$(docker run --rm -e REGISTRATION_CODE=<Registration_Code> ondataprod.azurecr.us/products/ondata-secureai-app:latest) && (echo $content | 
tee "<Registration_Output_File>" ; echo -e "SecureAI App registration succeeded") || echo $content

\`\`\`

e. For each data source, you need to get a new registration code and run the registration command. Select the \`Registration Code\` tab and click on the \`Add Registration Code\` 
button under the \`Data Source Registration\` section to get a registration code for the data source  
f. Launch a PowerShell or Bash command prompt and run the following command to register the data source. Use the registration code you have just created to replace the 
<Registration_Code> in the command line. As the registration information needs to be stored in a registration output file, please replace the <Registration_Output_File> in the 
command line with a full path to the designated registration output file, such as \`C:/SecureAI/Secrets/secureai_mcpserver_info_hospitaldemo.txt\`. Please note that the folder for the 
registration output file (\`C:/SecureAI/Secrets/\` in this example) should already exist on your file system. If not, please create that folder first  

PowerShell:
\`\`\`bash
@{ content= $(docker run --rm -e REGISTRATION_CODE=<Registration_Code> ondataprod.azurecr.us/products/ondata-secureai-mcpserver:latest); success= $? } | \`
%{ if ($_.success) { Set-Content -Path "<Registration_Output_File>" -Value $_.content; Write-Host "Data source registration succeeded!" } else { Write-Error $_.content } }

\`\`\`

Bash:
\`\`\`bash
content=$(docker run --rm -e REGISTRATION_CODE=<Registration_Code> ondataprod.azurecr.us/products/ondata-secureai-mcpserver:latest) && (echo $content | 
tee "<Registration_Output_File>" ; echo -e "Data source registration succeeded") || echo $content

\`\`\`

g. Repeat the two steps (e. and f.) above for each data source to get all data sources registered  
  
## Step 4 - Configure and Deploy SecureAI Containers
After the SecureAI App and all associated data sources are registered, you can deploy the SecureAI App container and associated data source containers. Below are the steps to 
configure and deploy the SecureAI containers:  
a. For each data source, run the OnData Encryption Tool to capture the database schema and store it to the OnData Portal. You can download a version of the tool for either 
Windows or Linux operating systems from the [**Product Releases**](../../../release/encryptiontool) page. The Windows version offers a graphical user interface for more 
convenient access. For details on how to use the OnData Encryption Tool, please refer to documentation regarding how to use the 
[**OnData Encryption Tool**](reference_doc#securedb/ondata_encryption/use_ondata_encryption_tool) to capture the database schema  
b. After the database schema is captured, login to the \`OnData Portal\` and navigate to \`Services\` -> \`Encryption\`. Under the \`Encryption Mappings\` tab, search for the database 
and click on \`Update Mappings\` to view the encryption mappings. For each data field in each table, review and adjust the data classification, encryption rule, and masking rule 
as needed. **Note**: The OnData Encryption Tool will automatically identify and classify sensitive data columns. Please review and adjust as needed to ensure the data 
classifications, encryption rules and masking rules are appropriately configured for the data columns in your database.  
c. If you would like to get all sensitive data in the database encrypted, you can perform data encryption using the OnData Encryption Tool. Please note that this step is 
optional. SecureAI will still protect your sensitive data even you do not encrypt your sensitive data in your database. If you choose not to encrypt your data, you can skip 
the encryption process  
d. After the database schemas for all databases are captured, you can configure a database driver client at the OnData Portal for later use. Below are the steps:  
* Login to the OnData Portal and navigate to \`Settings\` -> \`Organization\`
* Select the \`Clients\` tab and under the \`Database Driver\` sub tab, select the \`Active\` page. Click on the \`Add New\` button 
* Provide a client name and optional description, and click on \`Save\` to add a new database driver client. Please make sure you save a copy of the \`Client Id\` and 
the \`Client Secret\` values for later use. They won't be available once you navigate away from the current page  
* For the newly created database driver client, select the "+" sign under \`Actions\` and select the \`Assign Resources\` action 
* Under the \`Database\` tab, select all data sources associated with this SecureAI App deployment, and click on the \`Save\` button  
**Note**: If you want to add more data sources for the same database driver client later on, you can revisit this page and assign additional data sources. The associated 
MCP Servers may take up to three minutes to sync up with the latest data source assignment and show an online status.  

e. After completing all steps above, you can deploy the SecureAI App and associated data sources. Assume we are using Docker Compose for container orchestration, below is a 
sample Docker compose file for the deployment. Please note the following information:  
* The container registration files from Step 3 above are used under the \`secrets\` section. We have two data sources registered in the example below
* For the SecureAI App container, we need to configure the SSL certificate and the PostgreSQL databases
* For each data source (MCP Server) container, we need to configure the database driver client ID and client secret. All data sources can share the same client ID and client secret
* For each data source (MCP Server) container, we need to configure database type, database connectivity (database server address, database name, and credentials), and database 
driver operating mode (i.e., \`MCP_OperatingMode\`). If the database is encrypted in Step 4.c above, the database driver operating mode should be configured as \`Decryption\`. If the 
database encryption is skipped in Step 4.c above, the database driver operating mode should be configured as \`Encryption\`  
* For each data source (MCP Server) container, we can optionally configure the \`MCP_AllowUpdate\` option to specify whether the \`INSERT/UPDATE/DELETE\` SQL operations are allowed. By default, 
this option is set to false and only the \`SELECT\` SQL operations are allowed for this data source  
  
\`\`\`bash
networks:
  default:
    name: ondata-secureai-network

secrets:
  secureai_app_info:
    file: C:/SecureAI/Secrets/secureai_app_info.txt
  secureai_mcpserver_info_hospitaldemo:
    file: C:/SecureAI/Secrets/secureai_mcpserver_info_hospitaldemo.txt
  secureai_mcpserver_info_northwind:
    file: C:/SecureAI/Secrets/secureai_mcpserver_info_northwind.txt

services:
  secureai-app:
    image: ondataprod.azurecr.us/products/ondata-secureai-app:latest
    container_name: ondata-secureai-app
    ports:
      - 443:443
    environment:
      - ASPNETCORE_URLS=https://+:443
      - ASPNETCORE_Kestrel__Certificates__Default__Password=<Password for SSL certificate>
      - ASPNETCORE_Kestrel__Certificates__Default__Path=/https/<SSL certificate in .pfx format>
      - ConnectionStrings__DefaultConnection=<PostgreSQL database connection string to SecureAI database>;
      - ConnectionStrings__HangfireConnection=<PostgreSQL database connection string to Hangfire database>;
      - SecureAiName=SecureAI-Demo
      - SecureAiConfig__Url=https://localhost:443
      - SecureAiConfig__ExtractApi=http://extract-api:8000
      # Other optional configuration entries, with default values specified below
      # - ConversationRetentionDays=60
      # - ConversationHardDelete=false
      # - SecureAiConfig__RefreshTokenExpiresMinutes=1440
    secrets:
      - source: secureai_app_info
        target: /secrets/client_info
    volumes:
      - d:/secureai/certs/https:/https:ro

  secureai-mcpserver-hospitaldemo:
    image: ondataprod.azurecr.us/products/ondata-secureai-mcpserver:latest
    container_name: secureai-mcpserver-hospitaldemo
    # Optional port configuration
    # If not configured, only the SecureAI App in this Docker Compose file can access this MCP server by name (http://{MCP_Server_Name}, 
    #    such as http://secureai-mcpserver-hospitaldemo)
    # If configured, a SecureAI App can connect to this MCP server using the host IP address and the configured host_port below
    # ports:
    #   - <host_port>:80
    environment:
      - MCP_DriverClientId=<Database driver client ID>
      - MCP_DriverSecret=<Database driver client secret>
      # The complete list of supported database types are: 
      # MYSQL = 1, POSTGRESQL = 2, MSSQL = 3, MONGODB = 4, MARIADB = 5, ORACLE = 6, DB2 = 7, HANA = 8, SNOWFLAKE = 9, COSMOSDB = 10
      - MCP_DatabaseType=MSSQL
      # The OperatingMode can be Encryption or Decryption
      - MCP_OperatingMode=Encryption
      - MCP_DatabaseAddress=host.docker.internal
      - MCP_DatabaseName=HospitalDemo
      - MCP_DatabaseUser=<database username>
      - MCP_DatabasePassword=<database password>
      # Other optional configuration entries, with default values specified below
      # - MCP_DatabasePort=<database default port>
      # - MCP_DatabaseIntegratedSecurity=false
      # - MCP_MaxQueryPageSize=200
      # - MCP_DatabaseTNSServiceName=<TNS service name, for Oracle only>
      # - MCP_DatabaseTNSAdmin=<TNS admin, for Oracle only>
      # - MCP_DatabaseSchemas=<comma separated list of schema names, for Oracle only>
      # - MCP_OnDataDBName=<OnData database name, for Oracle only>
      #
      # The MCP_AllowUpdate option is used to specify whether the insert/update/delete operations are allowed
      # Default value is false, which means only the select operation is allowed for this data source
      # - MCP_AllowUpdate=false  
    secrets:
      - source: secureai_mcpserver_info_hospitaldemo
        target: /secrets/client_info
        
  secureai-mcpserver-northwind:
    image: ondataprod.azurecr.us/products/ondata-secureai-mcpserver:latest
    container_name: secureai-mcpserver-northwind
    # Optional port configuration
    # If not configured, only the SecureAI App in this Docker Compose file can access this MCP server by name (http://{MCP_Server_Name}, 
    #    such as http://secureai-mcpserver-hospitaldemo)
    # If configured, a SecureAI App can connect to this MCP server using the host IP address and the configured host_port below
    # ports:
    #   - <host_port>:80    
    environment:
      - MCP_DriverClientId=<Database driver client ID>
      - MCP_DriverSecret=<Database driver client secret>
      # The complete list of supported database types are: 
      # MYSQL = 1, POSTGRESQL = 2, MSSQL = 3, MONGODB = 4, MARIADB = 5, ORACLE = 6, DB2 = 7, HANA = 8, SNOWFLAKE = 9, COSMOSDB = 10      
      - MCP_DatabaseType=MSSQL
      # The OperatingMode can be Encryption or Decryption      
      - MCP_OperatingMode=Decryption
      - MCP_DatabaseAddress=host.docker.internal
      - MCP_DatabaseName=Northwind
      - MCP_DatabaseUser=<database username>
      - MCP_DatabasePassword=<database password>
      # Other optional configuration entries, with default values specified below
      # - MCP_DatabasePort=<database default port>
      # - MCP_DatabaseIntegratedSecurity=false
      # - MCP_MaxQueryPageSize=200
      # - MCP_DatabaseTNSServiceName=<TNS service name, for Oracle only>
      # - MCP_DatabaseTNSAdmin=<TNS admin, for Oracle only>
      # - MCP_DatabaseSchemas=<comma separated list of schema names, for Oracle only>
      # - MCP_OnDataDBName=<OnData database name, for Oracle only>
      #
      # The MCP_AllowUpdate option is used to specify whether the INSERT/UPDATE/DELETE SQL operations are allowed
      # Default value is false, which means only the SELECT SQL operations are allowed for this data source
      # - MCP_AllowUpdate=false   
    secrets:
      - source: secureai_mcpserver_info_northwind
        target: /secrets/client_info   

  extract-api:
    image: ondataprod.azurecr.us/products/xberg:latest
    container_name: extract-xberg
    restart: unless-stopped
    ports:
      - "8082:8000"
    environment:
      - RUST_LOG=info

\`\`\`
  
f. Deploy the SecureAI App and associated data sources with the following command:
  
\`\`\`bash
docker compose -f ".\\docker-compose.prod.yml" up -d

\`\`\`
  
**Note**: If any of the secrets configuration files has invalid values or the path to a secrets configuration file is invalid, you need to delete the previous 
container instance and recreate it. In that case, you may use the following command to force the recreation of that container instance.  

\`\`\`bash
docker compose -f ".\\docker-compose.prod.yml" up -d --force-recreate {service name}

\`\`\`
  

After the SecureAI App and associated data sources are deployed, you can follow the instructions to [**Configure SecureAI App**](reference_doc#secureai/installation_and_configuration/configure_secureai_app). 
  
    `
  
export default content;
